import React from 'react';
import Markdown from '@scale/frontend-lib/markdown';

export const getSectionTitle = (title: string): React.ReactNode => (
  <span dangerouslySetInnerHTML={{ __html: Markdown.renderInline(title) }} />
);

interface HelperTextProps {
  oneClickUrlForReviewer?: string;
  description?: string;
  chars?: number;
  tokens?: number;
  minChars?: number;
  maxChars?: number;
  minTokens?: number;
  maxTokens?: number;
  showWordCounter?: boolean;
}

export const getHelperText = (props: HelperTextProps): React.ReactNode => {
  const oneClickUrlForReviewer = props.oneClickUrlForReviewer ? (
    <a href={props.oneClickUrlForReviewer} target="_blank" rel="noreferrer noopener">
      Click here to open the URL above in a new tab
    </a>
  ) : null;
  const fieldHelperText = props.description ? (
    <span
      style={{ display: 'inline-block' }}
      dangerouslySetInnerHTML={{ __html: Markdown.renderInline(props.description) }}
    />
  ) : null;
  return (
    <span style={{ display: 'block', flexDirection: 'row', justifyContent: 'space-between' }}>
      <span style={{ display: 'block' }}>{oneClickUrlForReviewer}</span>
      <span style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <span style={{ display: 'inline-block' }}>{fieldHelperText}</span>
        {getTextLengthHelperText(props)}
      </span>
    </span>
  );
};

const getTextLengthHelperText = (props: HelperTextProps): React.ReactNode => {
  const { chars, maxChars, showWordCounter, tokens, maxTokens, minTokens } = props;
  if (!!showWordCounter && maxTokens !== undefined && tokens !== undefined) {
    const fontColor = getWordCounterFontColor(tokens, minTokens, maxTokens);
    return (
      <span style={{ display: 'inline-block', color: fontColor, textAlign: 'center' }}>
        {tokens}/{maxTokens} words
      </span>
    );
  } else if (maxChars !== undefined && chars !== undefined) {
    return (
      <span style={{ display: 'inline-block' }}>
        {chars}/{maxChars}
      </span>
    );
  } else if (!!showWordCounter && tokens !== undefined) {
    return <span style={{ display: 'inline-block', textAlign: 'center' }}>{tokens} words</span>;
  }
  return undefined;
};

const getWordCounterFontColor = (tokens?: number, minTokens?: number, maxTokens?: number) => {
  if (tokens === undefined || !minTokens || !maxTokens) {
    return undefined;
  }
  if (tokens < minTokens) {
    return 'red';
  } else if (tokens > minTokens * 2) {
    return 'green';
  } else {
    return 'orange';
  }
};

export const getExtraContent = (extraContent: string): React.ReactNode => (
  <span dangerouslySetInnerHTML={{ __html: Markdown.renderInline(extraContent) }} />
);

export const addCodeFieldsToMarkdown = (value: string, sourceCodeList: string[]) => {
  let markdownValue = value;
  sourceCodeList.forEach((sourceCode: string, idx: number) => {
    markdownValue = markdownValue.replace(
      `{{code_${idx + 1}}}`,
      `\n\`\`\`\n${sourceCode}\n \`\`\`\n`,
    );
  });
  return markdownValue;
};

export const getHelperDescriptionForMinMaxSelections = (
  minSelections: number | undefined,
  maxSelections: number | undefined,
): string | undefined => {
  if (minSelections === undefined && maxSelections === undefined) {
    return undefined;
  }
  return `Select a ${
    minSelections !== undefined
      ? 'minimum of ' +
        minSelections +
        ' choice' +
        (minSelections === 1 ? '' : 's') +
        (maxSelections !== undefined ? ';' : '')
      : ''
  } ${
    maxSelections !== undefined
      ? 'maximum of ' + maxSelections + ' choice' + (maxSelections === 1 ? '' : 's')
      : ''
  }`;
};
